/**
 * mobile-onboarding.tsx
 *
 * First-run mobile onboarding — "Odisseia" theme, irmã das cenas-recompensa
 * de `components/moments.tsx` (noite, brasa/vela, dossiê de papel).
 * Shown at "/" when the user is not authenticated and 'onboarding-seen' is
 * not set in localStorage (native app only — see App.tsx RootRoute).
 *
 * 4 telas, swipe horizontal com física (drag + spring), parallax leve
 * (fundo se move menos que o conteúdo) e dots com pílula deslizante:
 *   1. "A noite é sua"            — céu com estrelas em cascata + lua
 *   2. "Uma vela por noite"        — a vela do ritual diário acende
 *   3. "Seu universo ganha vida"   — ficha de papel (dossiê) sendo arquivada
 *   4. "Comece sua jornada"        — CTA: Criar conta / Já tenho conta
 *
 * The branded launch animation (LaunchAnimation component) plays separately
 * in App.tsx on every native cold start, so this screen goes straight to
 * the carousel without a duplicated intro.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useReducedMotion,
  animate,
  type PanInfo,
  type MotionValue,
} from 'motion/react';
import { ChevronRight } from 'lucide-react';
import { BornCandle } from '@/components/moments';
import { LangSelect } from '@/components/lang-select';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAILY_GOAL = 500;
const FALLBACK_VW = 375;

const SLIDE_IDS = ['night', 'candle', 'universe', 'cta'] as const;
type SlideId = (typeof SLIDE_IDS)[number];

const DRAG_SPRING = { type: 'spring' as const, stiffness: 220, damping: 26, mass: 0.9 };
const OFFSET_THRESHOLD = 50;
const VELOCITY_THRESHOLD = 450;

/** Estrelas do céu — posições determinísticas (nunca Math.random), acendem
 *  em cascata seguindo uma diagonal (top-left → bottom-right). */
const NIGHT_STARS = Array.from({ length: 34 }, (_, i) => {
  const left = (i * 53) % 100;
  const top = (i * 31) % 68 + 4;
  const size = [1.3, 1.8, 2.4][i % 3];
  const delay = ((left + top) / 172) * 1.6;
  return { left, top, size, delay };
});

/** Partículas ✦ sutis da tela final — offsets determinísticos. */
const CTA_PARTICLES = [
  { left: 6, top: 14, delay: 0.1 },
  { left: 92, top: 10, delay: 0.4 },
  { left: 8, top: 46, delay: 0.7 },
  { left: 90, top: 52, delay: 0.25 },
  { left: 6, top: 80, delay: 0.55 },
  { left: 92, top: 76, delay: 0.85 },
] as const;

function springOrInstant(reduced: boolean) {
  return reduced
    ? { duration: 0.18 }
    : { type: 'spring' as const, stiffness: 240, damping: 26 };
}

// ---------------------------------------------------------------------------
// useViewportWidth — tracks window width for the drag track math
// ---------------------------------------------------------------------------

function useViewportWidth(): number {
  const [width, setWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : FALLBACK_VW,
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return width;
}

// ---------------------------------------------------------------------------
// Shared slide chrome — eyebrow / title / subtitle (irmã de SceneText)
// ---------------------------------------------------------------------------

function SlideEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="font-mono text-[10px] uppercase tracking-[0.34em]"
      style={{ color: 'var(--ember)' }}
    >
      ✦ {children}
    </p>
  );
}

function SlideTitle({ children, size = 26 }: { children: React.ReactNode; size?: number }) {
  return (
    <h2
      className="font-serif font-medium leading-tight"
      style={{ fontSize: size, color: '#EBEEFA', maxWidth: 300 }}
    >
      {children}
    </h2>
  );
}

function SlideSubtitle({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="font-sans leading-relaxed text-muted-foreground"
      style={{ fontSize: 15, maxWidth: 280 }}
    >
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Slide 1 — "A noite é sua": céu estrelado em cascata + lua
// ---------------------------------------------------------------------------

function NightBackground({ isActive, reduced }: { isActive: boolean; reduced: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden>
      {NIGHT_STARS.map((star, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: star.size,
            height: star.size,
            background: '#F6E9C8',
            boxShadow: `0 0 ${star.size * 3}px rgba(246,233,200,0.75)`,
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={
            isActive
              ? {
                  opacity: reduced ? 0.75 : [0, 1, 0.65],
                  scale: 1,
                }
              : { opacity: 0, scale: 0 }
          }
          transition={{
            delay: reduced ? 0 : 0.15 + star.delay,
            duration: reduced ? 0.3 : 1,
          }}
        />
      ))}
      <motion.div
        className="absolute rounded-full"
        aria-hidden
        style={{
          width: 132,
          height: 132,
          top: '13%',
          left: '50%',
          marginLeft: -66,
          background: 'radial-gradient(circle at 36% 32%, #F6E9C8, #D8C48F 58%, #8A784E 96%)',
          boxShadow: '0 0 60px rgba(246,233,200,0.45), 0 0 140px rgba(246,233,200,0.18)',
        }}
        initial={{ opacity: 0, y: 24, scale: 0.85 }}
        animate={
          isActive ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 24, scale: 0.85 }
        }
        transition={{ delay: isActive ? 0.15 : 0, ...springOrInstant(reduced) }}
      >
        {!reduced && (
          <motion.span
            className="absolute inset-0 rounded-full"
            animate={isActive ? { opacity: [0.3, 0.6, 0.3] } : { opacity: 0 }}
            transition={{ duration: 4.4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ boxShadow: '0 0 70px rgba(246,233,200,0.5)' }}
          />
        )}
      </motion.div>
    </div>
  );
}

function NightSlideContent() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2.5 text-center">
      <SlideEyebrow>{t('onboarding.slide1Eyebrow', 'sua primeira noite')}</SlideEyebrow>
      <SlideTitle size={30}>{t('onboarding.slide1Title', 'A noite é sua')}</SlideTitle>
      <SlideSubtitle>
        {t(
          'onboarding.slide1Subtitle',
          'Um lugar silencioso onde suas histórias nascem, noite após noite — só você, a lua e a página em branco.',
        )}
      </SlideSubtitle>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slide 2 — "Uma vela por noite": o ritual diário de 500 palavras
// ---------------------------------------------------------------------------

function CandleBackground({ isActive }: { isActive: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden>
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 320,
          height: 320,
          top: '18%',
          left: '50%',
          marginLeft: -160,
          background: 'radial-gradient(circle at 50% 30%, rgba(223,201,147,0.22), transparent 68%)',
        }}
        animate={{ opacity: isActive ? 1 : 0 }}
        transition={{ duration: 0.6 }}
      />
    </div>
  );
}

function CandleSlideContent({ isActive, reduced }: { isActive: boolean; reduced: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="relative flex items-center justify-center" style={{ width: 160, height: 160 }}>
        <motion.div
          aria-hidden
          initial={{ scale: 0 }}
          animate={{ scale: isActive ? 2.6 : 0 }}
          transition={springOrInstant(reduced)}
          style={{ transformOrigin: 'center bottom' }}
        >
          <BornCandle delay={isActive ? 0.5 : 0} lit={isActive} />
        </motion.div>
      </div>
      <div className="flex flex-col items-center gap-2.5 text-center">
        <SlideEyebrow>{t('onboarding.slide2Eyebrow', 'ritual diário')}</SlideEyebrow>
        <SlideTitle>{t('onboarding.slide2Title', 'Uma vela por noite')}</SlideTitle>
        <SlideSubtitle>
          {t(
            'onboarding.slide2Subtitle',
            'A cada {{goal}} palavras, uma vela se acende — o registro silencioso do seu compromisso com a história.',
            { goal: DAILY_GOAL },
          )}
        </SlideSubtitle>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Slide 3 — "Seu universo ganha vida": a ficha do dossiê é arquivada
// ---------------------------------------------------------------------------

function TypewriterField({
  label,
  value,
  isActive,
  reduced,
  delay,
}: {
  label: string;
  value: string;
  isActive: boolean;
  reduced: boolean;
  delay: number;
}) {
  return (
    <div>
      <p
        className="font-mono text-[7px] uppercase tracking-[0.2em]"
        style={{ color: '#8A7A54' }}
      >
        {label}
      </p>
      <div className="mt-0.5 overflow-hidden">
        <motion.p
          initial={false}
          animate={{ width: reduced || isActive ? '100%' : '0%' }}
          transition={reduced ? { duration: 0 } : { delay, duration: 0.7, ease: 'linear' }}
          className="overflow-hidden whitespace-nowrap font-serif text-[16px] font-medium"
          style={{ color: '#241C36' }}
        >
          {value}
        </motion.p>
      </div>
    </div>
  );
}

function UniverseBackground({ isActive }: { isActive: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden>
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 300,
          height: 300,
          top: '10%',
          left: '50%',
          marginLeft: -150,
          background: 'radial-gradient(circle at 50% 30%, rgba(94,234,212,0.10), transparent 70%)',
        }}
        animate={{ opacity: isActive ? 1 : 0 }}
        transition={{ duration: 0.6 }}
      />
    </div>
  );
}

function UniverseSlideContent({ isActive, reduced }: { isActive: boolean; reduced: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <motion.div
        className="relative w-[246px] rounded-[3px_10px_10px_3px] px-5 py-5 select-none"
        style={{
          background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
          boxShadow: '0 30px 70px -18px rgba(0,0,0,0.75), 0 0 0 1px rgba(70,55,30,0.22)',
        }}
        initial={{ y: 26, opacity: 0, rotate: 3 }}
        animate={
          isActive ? { y: 0, opacity: 1, rotate: -1 } : { y: 26, opacity: 0, rotate: 3 }
        }
        transition={springOrInstant(reduced)}
      >
        <p
          className="font-mono text-[7.5px] uppercase tracking-[0.26em]"
          style={{ color: '#A8935F' }}
        >
          {t('onboarding.slide3DossierLabel', 'Arquivo do Universo')}
        </p>

        <div className="mt-3">
          <TypewriterField
            label={t('onboarding.slide3CharacterLabel', 'Personagem')}
            value={t('onboarding.slide3CharacterName', 'Ilyra Duskwood')}
            isActive={isActive}
            reduced={reduced}
            delay={0.5}
          />
        </div>

        <div
          className="mt-3 pt-3"
          style={{ borderTop: '1px dashed rgba(70,55,30,0.25)' }}
        >
          <TypewriterField
            label={t('onboarding.slide3PlaceLabel', 'Lugar')}
            value={t('onboarding.slide3PlaceName', 'O Farol de Vharenmoor')}
            isActive={isActive}
            reduced={reduced}
            delay={0.95}
          />
        </div>

        <div className="mt-4 flex flex-col gap-1.5" aria-hidden>
          {[68, 46].map((pct, i) => (
            <motion.span
              key={i}
              className="block h-[4px] rounded-full"
              style={{ background: 'rgba(70,55,30,0.15)' }}
              initial={{ width: 0 }}
              animate={{ width: isActive ? `${pct}%` : 0 }}
              transition={
                reduced
                  ? { duration: 0 }
                  : { delay: 1.5 + i * 0.15, duration: 0.45, ease: [0.22, 1, 0.36, 1] }
              }
            />
          ))}
        </div>

        <motion.span
          aria-hidden
          initial={{ scale: 2.4, opacity: 0, rotate: 6 }}
          animate={
            isActive
              ? { scale: 1, opacity: 1, rotate: -10 }
              : { scale: 2.4, opacity: 0, rotate: 6 }
          }
          transition={
            reduced
              ? { duration: 0.2 }
              : { delay: 1.35, type: 'spring', stiffness: 360, damping: 15 }
          }
          className="absolute -right-3 -top-3 rounded-full px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em]"
          style={{
            color: '#141005',
            background: 'linear-gradient(135deg, var(--ember-hot), var(--ember))',
            boxShadow: '0 6px 18px rgba(0,0,0,0.45), 0 0 20px rgba(223,201,147,0.4)',
          }}
        >
          ✦ {t('onboarding.slide3Stamp', 'Vivo')}
        </motion.span>
      </motion.div>

      <div className="flex flex-col items-center gap-2.5 text-center">
        <SlideEyebrow>{t('onboarding.slide3Eyebrow', 'seu universo')}</SlideEyebrow>
        <SlideTitle>{t('onboarding.slide3Title', 'Seu universo ganha vida')}</SlideTitle>
        <SlideSubtitle>
          {t(
            'onboarding.slide3Subtitle',
            'Personagens e lugares ganham fichas próprias, arquivadas automaticamente enquanto você escreve.',
          )}
        </SlideSubtitle>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Slide 4 — "Comece sua jornada": CTA final
// ---------------------------------------------------------------------------

function CtaBackground({ isActive, reduced }: { isActive: boolean; reduced: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden>
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 340,
          height: 340,
          top: '6%',
          left: '50%',
          marginLeft: -170,
          background: 'radial-gradient(circle at 50% 30%, rgba(223,201,147,0.18), transparent 70%)',
        }}
        animate={{ opacity: isActive ? 1 : 0 }}
        transition={{ duration: 0.6 }}
      />
      {CTA_PARTICLES.map((p, i) => (
        <motion.span
          key={i}
          className="absolute text-[11px]"
          style={{ left: `${p.left}%`, top: `${p.top}%`, color: 'var(--ember-hot)' }}
          initial={{ opacity: 0, scale: 0 }}
          animate={
            isActive
              ? reduced
                ? { opacity: 0.5, scale: 1 }
                : { opacity: [0.15, 0.75, 0.15], scale: [0.7, 1.15, 0.7], y: [0, -8, 0] }
              : { opacity: 0, scale: 0 }
          }
          transition={
            reduced
              ? { duration: 0.3 }
              : { duration: 2.6 + p.delay, repeat: Infinity, ease: 'easeInOut', delay: p.delay }
          }
        >
          ✦
        </motion.span>
      ))}
    </div>
  );
}

function CtaSlideContent({
  isActive,
  reduced,
  onCreateAccount,
  onGoToLogin,
}: {
  isActive: boolean;
  reduced: boolean;
  onCreateAccount: () => void;
  onGoToLogin: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <motion.span
        aria-hidden
        initial={{ scale: 0, opacity: 0 }}
        animate={isActive ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
        transition={
          reduced ? { duration: 0.2 } : { delay: 0.15, type: 'spring', stiffness: 220, damping: 14 }
        }
        className="text-[42px] leading-none"
        style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 18px rgba(246,233,200,0.75))' }}
      >
        ✦
      </motion.span>

      <div className="flex flex-col items-center gap-2.5 text-center">
        <SlideEyebrow>{t('onboarding.slide4Eyebrow', 'a jornada começa')}</SlideEyebrow>
        <SlideTitle size={28}>{t('onboarding.slide4Title', 'Comece sua jornada')}</SlideTitle>
        <SlideSubtitle>
          {t('onboarding.slide4Subtitle', 'Escreva seu livro com a IA que entende a sua visão.')}
        </SlideSubtitle>
      </div>

      <motion.div
        className="mt-1 flex w-full max-w-xs flex-col items-center gap-3"
        initial={{ opacity: 0, y: 16 }}
        animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
        transition={{
          delay: isActive ? 0.2 : 0,
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1] as const,
        }}
      >
        <motion.div className="w-full" whileTap={{ scale: 0.97 }}>
          <button
            type="button"
            onClick={onCreateAccount}
            className="btn-ember w-full"
            style={{ paddingTop: 16, paddingBottom: 16, fontSize: 11, borderRadius: 14 }}
          >
            {t('onboarding.createAccount', 'Criar conta')}
          </button>
        </motion.div>

        <button
          type="button"
          onClick={onGoToLogin}
          className="text-sm font-medium text-muted-foreground/70 transition-colors hover:text-foreground/80"
        >
          {t('onboarding.alreadyHaveAccount', 'Já tenho conta')}
        </button>

        <p className="mt-1 px-4 text-center text-[10px] leading-relaxed text-muted-foreground/40">
          {t('onboarding.legalAgreementPrefix', 'Ao criar conta você concorda com os')}{' '}
          <Link
            to="/termos"
            className="underline underline-offset-2 transition-colors hover:text-muted-foreground/70"
          >
            {t('onboarding.legalTerms', 'Termos')}
          </Link>{' '}
          {t('onboarding.legalAgreementJoin', 'e a')}{' '}
          <Link
            to="/privacidade"
            className="underline underline-offset-2 transition-colors hover:text-muted-foreground/70"
          >
            {t('onboarding.legalPrivacy', 'Privacidade')}
          </Link>
          .
        </p>
      </motion.div>
    </>
  );
}

// ---------------------------------------------------------------------------
// ParallaxLayer — background decoration for a slide that moves LESS than the
// foreground content during drag, but lands perfectly aligned at rest (the
// offset is a function of the slide's own distance from its resting spot,
// so it is always exactly 0 once that slide is centered in the viewport).
// ---------------------------------------------------------------------------

function ParallaxLayer({
  trackX,
  index,
  vw,
  factor,
  children,
}: {
  trackX: MotionValue<number>;
  index: number;
  vw: number;
  factor: number;
  children: React.ReactNode;
}) {
  const localDelta = useTransform(trackX, (v) => v + index * vw);
  const offsetX = useTransform(localDelta, (d) => d * -(1 - factor));
  return (
    <motion.div className="pointer-events-none absolute inset-0" style={{ x: offsetX }} aria-hidden>
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// ProgressDots — pílula deslizante via layoutId
// ---------------------------------------------------------------------------

function ProgressDots({
  total,
  active,
  onSelect,
}: {
  total: number;
  active: number;
  onSelect: (i: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-2"
      role="tablist"
      aria-label={t('onboarding.progressLabel', 'Progresso')}
    >
      {Array.from({ length: total }, (_, i) => {
        const isOn = i === active;
        return (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={isOn}
            aria-label={t('onboarding.dotLabel', 'Ir para a tela {{n}}', { n: i + 1 })}
            onClick={() => onSelect(i)}
            className="relative block h-[6px] overflow-hidden rounded-full"
            style={{
              width: isOn ? 24 : 6,
              background: 'rgba(168,184,232,0.22)',
              transition: 'width 0.3s cubic-bezier(0.22,1,0.36,1)',
            }}
          >
            {isOn && (
              <motion.span
                layoutId="onboarding-dot-pill"
                className="absolute inset-0 rounded-full"
                style={{ background: 'linear-gradient(90deg,var(--ember),var(--ember-hot))' }}
                transition={{ type: 'spring', stiffness: 340, damping: 30 }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MobileOnboarding (main export)
// ---------------------------------------------------------------------------

export function MobileOnboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const vw = useViewportWidth();
  const prefersReduced = useReducedMotion();
  const reduced = !!prefersReduced;

  const [activeIndex, setActiveIndex] = useState(0);
  const trackX = useMotionValue(0);
  const parallaxFactor = reduced ? 1 : 0.42;

  // Keep the draggable track synced to activeIndex — fires on mount, on
  // dot/next/skip navigation and on resize. ParallaxLayer derives its own
  // (always-zero-at-rest) offset directly from trackX, so it stays in sync.
  useEffect(() => {
    const controls = animate(trackX, -activeIndex * vw, reduced ? { duration: 0.2 } : DRAG_SPRING);
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, vw, reduced]);

  const markAsSeen = useCallback(() => {
    try {
      localStorage.setItem('onboarding-seen', '1');
    } catch {
      /* ignore */
    }
  }, []);

  const handleCreateAccount = useCallback(() => {
    markAsSeen();
    navigate('/app?auth=signup');
  }, [markAsSeen, navigate]);

  const handleGoToLogin = useCallback(() => {
    markAsSeen();
    navigate('/app');
  }, [markAsSeen, navigate]);

  const handleSkip = useCallback(() => {
    markAsSeen();
    navigate('/app');
  }, [markAsSeen, navigate]);

  const goTo = useCallback((i: number) => {
    setActiveIndex(Math.max(0, Math.min(i, SLIDE_IDS.length - 1)));
  }, []);

  const handleNext = useCallback(() => {
    setActiveIndex((s) => Math.min(s + 1, SLIDE_IDS.length - 1));
  }, []);

  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      setActiveIndex((current) => {
        if (info.offset.x < -OFFSET_THRESHOLD || info.velocity.x < -VELOCITY_THRESHOLD) {
          return Math.min(current + 1, SLIDE_IDS.length - 1);
        }
        if (info.offset.x > OFFSET_THRESHOLD || info.velocity.x > VELOCITY_THRESHOLD) {
          return Math.max(current - 1, 0);
        }
        return current;
      });
    },
    [],
  );

  const isLast = activeIndex === SLIDE_IDS.length - 1;
  const trackWidth = SLIDE_IDS.length * vw;

  const renderBackground = (id: SlideId, isActive: boolean) => {
    switch (id) {
      case 'night':
        return <NightBackground isActive={isActive} reduced={reduced} />;
      case 'candle':
        return <CandleBackground isActive={isActive} />;
      case 'universe':
        return <UniverseBackground isActive={isActive} />;
      case 'cta':
        return <CtaBackground isActive={isActive} reduced={reduced} />;
    }
  };

  const renderContent = (id: SlideId, isActive: boolean) => {
    switch (id) {
      case 'night':
        return <NightSlideContent />;
      case 'candle':
        return <CandleSlideContent isActive={isActive} reduced={reduced} />;
      case 'universe':
        return <UniverseSlideContent isActive={isActive} reduced={reduced} />;
      case 'cta':
        return (
          <CtaSlideContent
            isActive={isActive}
            reduced={reduced}
            onCreateAccount={handleCreateAccount}
            onGoToLogin={handleGoToLogin}
          />
        );
    }
  };

  return (
    /*
     * `dark` class forces all Tailwind CSS-var tokens (bg-card, text-foreground,
     * text-muted-foreground, bg-muted, border-border …) to resolve using the
     * moonlight/Odisseia dark theme values, independent of system preference —
     * the onboarding is always presented in a dark, nocturnal context.
     */
    <div
      className="dark flex h-dvh w-full select-none flex-col overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #0A101F 0%, #101A33 55%, #0A101F 100%)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      {/* ── Header: logo discreta + pular ── */}
      <div className="flex h-14 flex-none items-center justify-between px-6 pb-0 pt-3">
        <div className="flex items-center gap-2">
          <img
            src="/log-white.png"
            alt=""
            aria-hidden
            style={{ width: 20, height: 20, objectFit: 'contain', opacity: 0.6 }}
          />
          <span className="font-serif text-sm font-semibold text-foreground/65">
            reformu<span style={{ color: 'var(--ember)' }}>.</span>lat
          </span>
        </div>

        <div className="flex items-center gap-1">
          {/* Language selector — discreet, reuses the app-wide LangSelect
              dropdown so language choice persists via i18next-browser-languagedetector. */}
          <LangSelect className="text-foreground/60" />

          <AnimatePresence>
            {!isLast && (
              <motion.button
                key="skip"
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.22 }}
                type="button"
                onClick={handleSkip}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-muted-foreground/60 transition-transform active:scale-95"
              >
                {t('onboarding.skip', 'Pular')}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Slides area ── */}
      <div className="relative flex-1 overflow-hidden">
        <motion.div
          className="relative flex h-full"
          style={{ width: trackWidth, x: trackX }}
          drag="x"
          dragConstraints={{ left: -(SLIDE_IDS.length - 1) * vw, right: 0 }}
          dragElastic={{ left: 0.1, right: 0.1 }}
          onDragEnd={handleDragEnd}
        >
          {SLIDE_IDS.map((id, i) => {
            const isActive = i === activeIndex;
            return (
              <div key={id} className="relative h-full overflow-hidden" style={{ width: vw }}>
                {/* Background — parallax: moves less than the content during drag,
                    perfectly aligned with the foreground once this slide is at rest. */}
                <ParallaxLayer trackX={trackX} index={i} vw={vw} factor={parallaxFactor}>
                  {renderBackground(id, isActive)}
                </ParallaxLayer>

                {/* Foreground content */}
                <div
                  className="relative flex h-full flex-col items-center justify-center gap-6 px-8"
                  style={{ pointerEvents: isActive ? 'auto' : 'none' }}
                >
                  <motion.div
                    className="flex w-full flex-col items-center gap-6"
                    animate={{ scale: isActive ? 1 : 0.91, opacity: isActive ? 1 : 0.4 }}
                    transition={springOrInstant(reduced)}
                  >
                    {renderContent(id, isActive)}
                  </motion.div>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>

      {/* ── Footer: dots + próximo ── */}
      <div className="flex h-20 flex-none items-center justify-between px-8">
        <ProgressDots total={SLIDE_IDS.length} active={activeIndex} onSelect={goTo} />

        <AnimatePresence mode="wait">
          {!isLast ? (
            <motion.button
              key="next"
              type="button"
              onClick={handleNext}
              aria-label={t('onboarding.next', 'Próximo')}
              initial={{ opacity: 0, scale: 0.75 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.75 }}
              transition={{ type: 'spring', stiffness: 320, damping: 24 }}
              whileTap={{ scale: 0.86 }}
              className="flex shrink-0 items-center justify-center"
              style={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--ember), #C4A96B)',
                boxShadow: '0 4px 18px rgba(223,201,147,0.42)',
              }}
            >
              <ChevronRight size={22} color="#141005" />
            </motion.button>
          ) : (
            <motion.div
              key="spacer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0 }}
              style={{ width: 54, height: 54, flexShrink: 0 }}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
